'use client'

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Department, DepartmentInput } from '@garage/shared'
import { departmentInputSchema } from '@garage/shared'
import { Dialog, TextField, Button, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

interface DepartmentFormDialogProps {
  open: boolean
  department?: Department
  onClose: () => void
}

export function DepartmentFormDialog({ open, department, onClose }: DepartmentFormDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const isCreate = !department

  const [name, setName] = useState(department?.name ?? '')
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    if (open) {
      setName(department?.name ?? '')
      setError(undefined)
    }
  }, [open, department])

  const mutation = useMutation({
    mutationFn: async (input: DepartmentInput) =>
      isCreate ? api.departments.create(input) : api.departments.update(department!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.departments })
      queryClient.invalidateQueries({ queryKey: qk.workers() })
      snackbar.success(isCreate ? t('departmentForm.departmentAdded') : t('departmentForm.departmentUpdated'))
      onClose()
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message)
      snackbar.error(err instanceof ApiError ? err.message : t('common.errorOccurred'))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const input: DepartmentInput = { name }
    const result = departmentInputSchema.safeParse(input)
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? t('departmentForm.nameRequired'))
      return
    }

    setError(undefined)
    mutation.mutate(input)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('departmentForm.addDepartment') : t('departmentForm.editDepartment')}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" onClick={handleSubmit} loading={mutation.isPending}>
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit}>
        <TextField
          label={t('departmentForm.departmentName')}
          value={name}
          onChange={setName}
          error={error}
          placeholder={t('departmentForm.departmentNamePlaceholder')}
          fullWidth
          autoFocus
        />
      </form>
    </Dialog>
  )
}
